import { useCallback, useEffect, useState } from "react";
import { Pagination } from "@/components/Pagination";
import { usePaged } from "@/lib/usePaged";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { channelLabels } from "@/lib/sectorVisibility";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderTree, Plus, Pencil, Trash2, Loader2, Percent, X, UserCog, Users, Archive, ArchiveRestore } from "lucide-react";
import { BrandsPanel } from "@/components/BrandsPanel";
import { ChannelsManager } from "@/components/SalesChannelsDialog";

export default function Categories() {
  const { user: me, can, activeSector, activeSectorObj, allowedSectors } = useAuth();
  const secParam = activeSector && activeSector !== "full" ? activeSector : "";
  const chLabels = channelLabels(activeSectorObj);
  const pickableSectors = (allowedSectors || []).filter((s) => s.code !== "full");
  const isAdmin = me?.role === "admin";
  const canManage = can("category.manage");
  const canRemoveDisc = me?.role === "admin" || me?.role === "assistant";
  const [channel, setChannel] = useState("market");
  const [view, setView] = useState("active");
  const [hubTab, setHubTab] = useState("kategoriler");
  const [cats, setCats] = useState([]);
  const catsPaged = usePaged(cats, 20);
  const [discounts, setDiscounts] = useState([]);
  const [people, setPeople] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", subcategories: "", manager_email: "", assistant_emails: [], low_stock_threshold: "", sectors: [] });
  const [saving, setSaving] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);
  const [discCat, setDiscCat] = useState(null);
  const [discPct, setDiscPct] = useState(5);
  const [discSaving, setDiscSaving] = useState(false);

  const load = useCallback(() => api.get(`/categories?channel=${channel}&status=${view}${secParam ? `&sector=${secParam}` : ""}`).then((r) => setCats(r.data)).catch(() => {}), [channel, view, secParam]);
  const loadDiscounts = useCallback(() => api.get(`/categories/discounts?status=active&channel=${channel}`).then((r) => setDiscounts(r.data)).catch(() => {}), [channel]);
  useEffect(() => { load(); loadDiscounts(); }, [load, loadDiscounts]);
  useEffect(() => { if (canManage) api.get("/users/assignable").then((r) => setPeople(r.data)).catch(() => {}); }, [canManage]);
  const discOf = (name) => discounts.find((d) => d.category === name);
  const nameOf = (email) => { const p = people.find((x) => x.email === email); return p ? `${p.name} · ${p.role_label}` : email; };
  const sectorName = (code) => (allowedSectors || []).find((s) => s.code === code)?.name || code;

  const openDisc = (c) => { setDiscCat(c); setDiscPct(5); setDiscOpen(true); };
  const submitDisc = async () => {
    if (!discCat) return;
    setDiscSaving(true);
    try {
      const res = await api.post("/categories/discount", { category: discCat.name, channel_type: channel, pct: discPct });
      if (res.data?.pending_approval) toast.info(res.data.message || "İndirim önerisi onaya gönderildi");
      else toast.success(`"${discCat.name}" kategorisine %${discPct} indirim uygulandı`);
      setDiscOpen(false); loadDiscounts();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDiscSaving(false); }
  };
  const removeDisc = async (d) => {
    if (!window.confirm(`"${d.category}" kategorisindeki %${d.pct} indirim kaldırılsın mı?`)) return;
    try { await api.post(`/categories/discounts/${d.id}/remove`); toast.success("İndirim kaldırıldı"); loadDiscounts(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm({ name: "", subcategories: "", manager_email: "", assistant_emails: [], low_stock_threshold: "", sectors: secParam ? [secParam] : [] }); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, subcategories: (c.subcategories || []).join(", "),
      manager_email: c.manager_email || "", assistant_emails: c.assistant_emails || [],
      low_stock_threshold: c.low_stock_threshold ?? "", sectors: c.sectors || [] });
    setOpen(true);
  };

  const toggleSector = (code) => setForm((f) => ({
    ...f, sectors: f.sectors.includes(code) ? f.sectors.filter((s) => s !== code) : [...f.sectors, code],
  }));

  const toggleAssistant = (email) => setForm((f) => ({
    ...f, assistant_emails: f.assistant_emails.includes(email) ? f.assistant_emails.filter((e) => e !== email) : [...f.assistant_emails, email],
  }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Kategori adı zorunlu");
    setSaving(true);
    const subs = form.subcategories.split(",").map((s) => s.trim()).filter(Boolean);
    const body = {
      name: form.name.trim(), channel_type: channel, subcategories: subs,
      manager_email: form.manager_email || null,
      assistant_emails: form.assistant_emails.filter((e) => e && e !== form.manager_email),
      low_stock_threshold: form.low_stock_threshold !== "" && form.low_stock_threshold != null ? Number(form.low_stock_threshold) : null,
      sectors: form.sectors || [],
    };
    try {
      const res = editing ? await api.put(`/categories/${editing.id}`, body) : await api.post("/categories", body);
      if (res.data?.pending_approval) toast.info(res.data.message || "Değişiklik onaya gönderildi");
      else toast.success(editing ? "Kategori güncellendi" : "Kategori eklendi");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const archive = async (c) => {
    if (!window.confirm(`"${c.name}" kategorisi arşivlensin mi? Ürün seçim listelerinden gizlenir, istediğinizde geri yükleyebilirsiniz.`)) return;
    try { await api.post(`/categories/${c.id}/archive`); toast.success("Kategori arşivlendi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const restore = async (c) => {
    try { await api.post(`/categories/${c.id}/restore`); toast.success("Kategori geri yüklendi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const purge = async (c) => {
    if (!window.confirm(`"${c.name}" kategorisi KALICI olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    try { await api.delete(`/categories/${c.id}`); toast.success("Kategori kalıcı olarak silindi"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4 fade-in">
      <div className="flex items-center gap-2">
        <FolderTree className="h-5 w-5 text-brand-green" />
        <div><p className="overline">Katalog</p><h1 className="font-heading text-2xl font-bold text-slate-900">Katalog Yönetimi</h1></div>
      </div>
      <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200" data-testid="catalog-hub-tabs">
        {[["kategoriler", "Kategoriler"], ["markalar", "Markalar"], ["kanallar", "Kanallar"]].map(([v, l]) => (
          <button key={v} data-testid={`hub-tab-${v}`} onClick={() => setHubTab(v)}
            className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${hubTab === v ? "bg-brand-green text-white" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
        ))}
      </div>

      {hubTab === "markalar" && <BrandsPanel canManage={canManage} />}
      {hubTab === "kanallar" && (canManage ? <ChannelsManager /> : <p className="rounded-md bg-white p-6 text-center text-sm text-slate-400 ring-1 ring-inset ring-slate-200">Kanalları yönetme yetkiniz yok.</p>)}

      {hubTab === "kategoriler" && (
      <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
            {["market", "toptan"].map((c) => (
              <button key={c} data-testid={`cat-channel-${c}`} onClick={() => setChannel(c)}
                className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${channel === c ? "bg-brand-green text-white" : "text-slate-500"}`}>
                {c === "market" ? chLabels.market : chLabels.toptan}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-md bg-white p-1 ring-1 ring-inset ring-slate-200">
            {[["active", "Aktif"], ["archived", "Arşiv"]].map(([v, l]) => (
              <button key={v} data-testid={`cat-view-${v}`} onClick={() => setView(v)}
                className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${view === v ? "bg-slate-800 text-white" : "text-slate-500"}`}>
                {l}
              </button>
            ))}
          </div>
          {canManage && view === "active" && (
            <Button data-testid="cat-new-btn" onClick={openNew} className="bg-brand-green text-white hover:bg-brand-green-600">
              <Plus className="mr-1.5 h-4 w-4" /> Yeni Kategori
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cats.length === 0 && <p className="text-sm text-slate-400">Bu kanalda kategori yok.</p>}
        {catsPaged.slice.map((c) => (
          <div key={c.id} data-testid={`cat-card-${c.id}`} className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
            <div className="mb-2 flex items-start justify-between">
              <p className="font-heading font-bold text-slate-800">{c.name}</p>
              {canManage && (
                <div className="flex gap-1">
                  {view === "active" ? (<>
                    <button data-testid={`cat-edit-${c.id}`} onClick={() => openEdit(c)} className="rounded-sm p-1.5 text-slate-500 hover:bg-slate-100" title="Düzenle"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`cat-archive-${c.id}`} onClick={() => archive(c)} className="rounded-sm p-1.5 text-amber-600 hover:bg-amber-50" title="Arşivle"><Archive className="h-4 w-4" /></button>
                  </>) : (<>
                    <button data-testid={`cat-restore-${c.id}`} onClick={() => restore(c)} className="rounded-sm p-1.5 text-emerald-600 hover:bg-emerald-50" title="Geri Yükle"><ArchiveRestore className="h-4 w-4" /></button>
                    {isAdmin && <button data-testid={`cat-purge-${c.id}`} onClick={() => purge(c)} className="rounded-sm p-1.5 text-red-500 hover:bg-red-50" title="Kalıcı Sil"><Trash2 className="h-4 w-4" /></button>}
                  </>)}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {(c.subcategories || []).length === 0 && <span className="text-[11px] text-slate-400">Alt kategori yok</span>}
              {(c.subcategories || []).map((s, i) => (
                <span key={i} className="rounded-sm bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">{s}</span>
              ))}
            </div>

            {pickableSectors.length > 0 && (
              <div data-testid={`cat-sectors-${c.id}`} className="mt-2 flex flex-wrap items-center gap-1">
                {(c.sectors || []).length === 0 ? (
                  <span className="text-[11px] text-slate-400">Tüm sektörlerde görünür</span>
                ) : (c.sectors || []).map((s) => (
                  <span key={s} className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">{sectorName(s)}</span>
                ))}
              </div>
            )}

            {/* Sorumlular */}
            <div data-testid={`cat-responsible-${c.id}`} className="mt-3 space-y-1 border-t border-slate-100 pt-2">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <UserCog className="h-3 w-3 text-brand-green" />
                <span className="font-semibold text-slate-600">Sorumlu Yönetici:</span>
                {c.manager_email
                  ? <span className="truncate text-emerald-700" title={nameOf(c.manager_email)}>{nameOf(c.manager_email)}</span>
                  : <span className="text-slate-400">atanmadı</span>}
              </div>
              {(c.assistant_emails || []).length > 0 && (
                <div className="flex items-start gap-1.5 text-[11px] text-slate-500">
                  <Users className="mt-0.5 h-3 w-3 text-sky-500" />
                  <span className="flex flex-wrap gap-1">
                    {(c.assistant_emails || []).map((e) => (
                      <span key={e} className="rounded-sm bg-sky-50 px-1.5 py-0.5 text-sky-700" title={nameOf(e)}>{nameOf(e)}</span>
                    ))}
                  </span>
                </div>
              )}
            </div>

            {(() => {
              const d = discOf(c.name);
              if (d) return (
                <div data-testid={`cat-disc-active-${c.id}`} className="mt-3 flex items-center justify-between rounded-md bg-red-50 px-2 py-1.5 ring-1 ring-inset ring-red-200">
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600"><Percent className="h-3 w-3" /> %{d.pct} indirim aktif · ay sonuna kadar</span>
                  {canRemoveDisc && (
                    <button data-testid={`cat-disc-remove-${c.id}`} onClick={() => removeDisc(d)} className="rounded-sm p-0.5 text-red-500 hover:bg-red-100" title="İndirimi kaldır"><X className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              );
              if (canManage && view === "active") return (
                <button data-testid={`cat-disc-btn-${c.id}`} onClick={() => openDisc(c)}
                  className="mt-3 inline-flex items-center gap-1 rounded-md bg-brand-orange/10 px-2.5 py-1 text-[11px] font-semibold text-brand-orange hover:bg-brand-orange/20">
                  <Percent className="h-3 w-3" /> % İndirim Öner
                </button>
              );
              return null;
            })()}
          </div>
        ))}
      </div>

      <Pagination page={catsPaged.page} pageSize={catsPaged.pageSize} total={catsPaged.total}
        onPage={catsPaged.setPage} onPageSize={catsPaged.setPageSize} testid="categories-pagination" />
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cat-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Kategoriyi Düzenle" : "Yeni Kategori"}</DialogTitle>
            <DialogDescription>Sorumlu yönetici/yardımcı atarsanız bu kategorideki fiyat & indirim değişiklikleri onay için onlara gider. {!isAdmin && "Kaydettiğinizde değişiklik amirinizin onayına gönderilir."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Kategori Adı *</Label><Input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Örn. Meyve - Sebze" /></div>
            <div><Label>Alt Kategoriler</Label><Input data-testid="cat-subs" value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} className="mt-1" placeholder="Taze, Dondurulmuş, Kuru" /></div>

            <div>
              <Label>Düşük Stok Eşiği (adet)</Label>
              <Input data-testid="cat-low-stock" type="number" min="0" value={form.low_stock_threshold}
                onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })} className="mt-1" placeholder="Genel varsayılan kullanılır" />
              <p className="mt-1 text-[11px] text-slate-400">Bu kategorideki ürünler için düşük stok eşiği. Ürün kartındaki eşik önceliklidir.</p>
            </div>

            {pickableSectors.length > 0 && (
              <div>
                <Label className="flex items-center gap-1.5"><FolderTree className="h-3.5 w-3.5 text-indigo-500" /> Sektörler (bu kategori hangi sektörlerde görünsün)</Label>
                <div data-testid="cat-sectors-picker" className="mt-1 flex flex-wrap gap-1.5">
                  {pickableSectors.map((s) => {
                    const on = form.sectors.includes(s.code);
                    return (
                      <button type="button" key={s.code} data-testid={`cat-sector-opt-${s.code}`} onClick={() => toggleSector(s.code)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${on ? "bg-indigo-600 text-white ring-indigo-600" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"}`}>
                        {s.name}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Boş bırakılırsa <b>tüm sektörlerde</b> görünür. Sektör seçerseniz yalnızca o sektör(ler) aktifken kategori ve ürünleri listelenir (örn. eczane, market ürünlerini görmez).</p>
              </div>
            )}

            <div>
              <Label className="flex items-center gap-1.5"><UserCog className="h-3.5 w-3.5 text-brand-green" /> Sorumlu Yönetici</Label>
              <select data-testid="cat-manager" value={form.manager_email}
                onChange={(e) => setForm((f) => ({ ...f, manager_email: e.target.value, assistant_emails: f.assistant_emails.filter((x) => x !== e.target.value) }))}
                className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                <option value="">— Seçilmedi —</option>
                {people.map((p) => <option key={p.email} value={p.email}>{`${p.name} · ${p.role_label}`}</option>)}
              </select>
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-sky-500" /> Yardımcılar (birden fazla seçilebilir)</Label>
              <div data-testid="cat-assistants" className="mt-1 max-h-44 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2">
                {people.filter((p) => p.email !== form.manager_email).map((p) => (
                  <label key={p.email} data-testid={`cat-assistant-opt-${p.email}`}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 text-sm hover:bg-white">
                    <input type="checkbox" checked={form.assistant_emails.includes(p.email)} onChange={() => toggleAssistant(p.email)} className="h-4 w-4 accent-brand-green" />
                    <span className="text-slate-700">{p.name}</span>
                    <span className="text-[11px] text-slate-400">· {p.role_label}</span>
                  </label>
                ))}
                {people.length === 0 && <p className="px-2 py-1 text-xs text-slate-400">Kullanıcı listesi yüklenemedi.</p>}
              </div>
            </div>

            <Button data-testid="cat-save" onClick={save} disabled={saving} className="w-full bg-brand-orange text-white hover:bg-orange-600">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editing ? "Kaydet" : "Ekle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent data-testid="cat-disc-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading"><Percent className="h-5 w-5 text-brand-orange" /> Aylık Kategori İndirimi</DialogTitle>
            <DialogDescription>
              <b>{discCat?.name}</b> kategorisindeki tüm ürünlere ay sonuna kadar geçerli indirim uygulayın. Kategori başına ayda en fazla 1 kez.
              {!isAdmin && " Öneriniz akran-oylaması ve Yönetici onayından sonra uygulanır."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">İndirim Oranı</Label>
              <div className="grid grid-cols-4 gap-2">
                {[3, 5, 8, 10].map((p) => (
                  <button key={p} data-testid={`disc-pct-${p}`} onClick={() => setDiscPct(p)}
                    className={`rounded-md py-2.5 text-sm font-bold transition-colors ${discPct === p ? "bg-brand-orange text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    %{p}
                  </button>
                ))}
              </div>
            </div>
            <Button data-testid="cat-disc-submit" onClick={submitDisc} disabled={discSaving} className="w-full bg-brand-green text-white hover:bg-brand-green-600">
              {discSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {isAdmin ? `%${discPct} İndirimi Uygula` : `%${discPct} İndirimi Öner`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
